
import { useState, useRef, useEffect } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";

interface CustomMultiSelectFilterProps {
  label: string;
  options: string[];
  selected: string[];
  onSelectionChange: (selected: string[]) => void;
  placeholder?: string;
  formatDisplay?: (value: string) => string;
  onOpenChange?: (open: boolean) => void;
  deferChangeUntilClose?: boolean;
}

const CustomMultiSelectFilter = ({ 
  label, 
  options = [], 
  selected = [], 
  onSelectionChange,
  placeholder = "Select options...",
  formatDisplay,
  onOpenChange,
  deferChangeUntilClose = false
}: CustomMultiSelectFilterProps) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [localSelected, setLocalSelected] = useState<string[]>(selected || []);
  // Snapshot of what was selected when the dropdown opened. Used to push selected
  // options to the top without making items jump around while the user clicks.
  const [selectedAtOpen, setSelectedAtOpen] = useState<Set<string>>(new Set());
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Sync localSelected from props when closed
  useEffect(() => {
    if (!open) {
      setLocalSelected(selected);
    }
  }, [selected.join(','), open]);

  // Clean and filter options
  const safeOptions = options.filter(option => 
    option && typeof option === 'string' && option.trim().length > 0
  );

  const safeSelected = localSelected.filter(item => 
    item && typeof item === 'string' && item.trim().length > 0
  );

  // Filter options based on search term
  const filteredOptions = safeOptions.filter(option => {
    const displayValue = formatDisplay ? formatDisplay(option) : option;
    return displayValue.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Show options selected when the dropdown opened first; preserve original order within each group.
  const orderedOptions = [...filteredOptions].sort((a, b) => {
    const aSel = selectedAtOpen.has(a) ? 0 : 1;
    const bSel = selectedAtOpen.has(b) ? 0 : 1;
    return aSel - bSel;
  });

  const toggleOption = (option: string) => {
    if (!option) return;
    
    const newSelected = localSelected.includes(option)
      ? localSelected.filter(item => item !== option)
      : [...localSelected, option];
    
    setLocalSelected(newSelected);
    // Keep parent state in sync immediately so top-level "Done" always sees latest values.
    onSelectionChange(newSelected);
  };

  const clearAll = () => {
    setLocalSelected([]);
    onSelectionChange([]);
  };

  const selectAll = () => {
    setLocalSelected(filteredOptions);
    onSelectionChange(filteredOptions);
  };

  const removeItem = (item: string, event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    
    const newSelected = localSelected.filter(selectedItem => selectedItem !== item);
    setLocalSelected(newSelected);
    onSelectionChange(newSelected);
  };

  // Focus search input and snapshot current selection when opened
  useEffect(() => {
    if (open) {
      setSelectedAtOpen(new Set(selected));
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  }, [open]);

  // Reset search when closed
  useEffect(() => {
    if (!open) {
      setSearchTerm("");
    }
  }, [open]);

  const handleOpenChange = (next: boolean) => {
    // Keep this close-time commit for compatibility with existing deferred usage.
    if (open && !next && deferChangeUntilClose) onSelectionChange(localSelected);
    setOpen(next);
    onOpenChange?.(next);
  };

  return (
    <div className="w-full">
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-between h-auto min-h-[40px] text-left font-normal p-3 relative hover:bg-blue-50 transition-colors"
          >
            <div className="flex flex-wrap gap-1 items-center min-h-[20px] flex-1">
              {safeSelected.length === 0 ? (
                <span className="text-muted-foreground">{label}</span>
              ) : (
                <>
                  {safeSelected.slice(0, 2).map((item) => (
                    <Badge 
                      key={item} 
                      variant="secondary" 
                      className="text-xs h-6 px-2 bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors"
                    >
                      {formatDisplay ? formatDisplay(item) : item}
                      <button
                        type="button"
                        className="ml-1 h-3 w-3 cursor-pointer hover:text-red-500 transition-colors flex items-center justify-center"
                        onClick={(e) => {
                          console.log('X button clicked for item:', item);
                          removeItem(item, e);
                        }}
                        onMouseDown={(e) => {
                          console.log('X button mousedown for item:', item);
                          e.stopPropagation();
                        }}
                        aria-label={`Remove ${item}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                  {safeSelected.length > 2 && (
                    <Badge variant="secondary" className="text-xs h-6 px-2 bg-blue-100 text-blue-800">
                      +{safeSelected.length - 2} more
                    </Badge>
                  )}
                </>
              )}
            </div>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full min-w-[280px] p-0" align="start">
          <div className="p-3 border-b">
            <input
              ref={searchInputRef}
              type="text"
              placeholder={`Search ${label.toLowerCase()}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div className="max-h-60 overflow-y-auto">
            <div className="p-2 border-b space-y-1 bg-gray-50">
              {filteredOptions.length > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={selectAll}
                  className="w-full text-blue-600 hover:text-blue-700 hover:bg-blue-50 justify-start"
                >
                  Select All ({filteredOptions.length})
                </Button>
              )}
              {safeSelected.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAll}
                  className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 justify-start"
                >
                  Clear All ({safeSelected.length})
                </Button>
              )}
            </div>
            
            {orderedOptions.length > 0 ? (
              <div className="p-1">
                {orderedOptions.map((option) => {
                  const displayValue = formatDisplay ? formatDisplay(option) : option;
                  return (
                    <div
                      key={option}
                      onClick={() => toggleOption(option)}
                      className="flex items-center space-x-2 px-3 py-2 cursor-pointer hover:bg-blue-50 rounded-md transition-colors"
                    >
                      <div className={`w-4 h-4 border rounded flex items-center justify-center transition-colors ${
                        safeSelected.includes(option) 
                          ? 'bg-blue-600 border-blue-600' 
                          : 'border-gray-300 hover:border-blue-400'
                      }`}>
                        {safeSelected.includes(option) && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <span className="flex-1 text-sm">{displayValue}</span>
                      {safeSelected.includes(option) && (
                        <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800">
                          Selected
                        </Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-4 text-center text-sm text-gray-500">
                {searchTerm ? 'No matching options' : 'No options available'}
              </div>
            )}
          </div>
          
          {safeSelected.length > 0 && (
            <div className="p-2 border-t bg-blue-50">
              <div className="text-xs text-blue-700 text-center font-medium">
                {safeSelected.length} item{safeSelected.length !== 1 ? 's' : ''} selected
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default CustomMultiSelectFilter;
